import { ArrowRight, CalendarCheck2, CalendarPlus, CheckCircle2, Clock3, Globe2, Plus, Trash2, UsersRound } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/common/Button';
import { EmptyState } from '../../components/common/EmptyState';
import { FormField } from '../../components/common/FormField';
import { LoadingState } from '../../components/common/LoadingState';
import { Modal } from '../../components/common/Modal';
import { StatusBadge } from '../../components/common/StatusBadge';
import { createIntake, getCourses, getIntakes } from '../../firebase/firestore';
import { useAuth } from '../../hooks/useAuth';
import { useTenant } from '../../hooks/useTenant';
import { useToast } from '../../contexts/ToastContext';
import { formatDate } from '../../utils/date';
import { createDefaultIntakeSession, formatIntakeSessionsSummary, WEEKDAY_OPTIONS } from '../../utils/intakeSchedule';

function createInitialValues(courseId = '') {
  return {
    name: '',
    courseId,
    type: 'cohort',
    status: 'open',
    startDate: '',
    endDate: '',
    capacity: '',
    publicVisible: true,
    sessions: [createDefaultIntakeSession()],
  };
}

export function IntakesPage({
  title = 'Intakes',
  description = 'Create open intakes or cohorts that public students can join.',
  actionLabel = 'New Intake',
  courseEmptyTitle = 'Create a course before adding intakes.',
  courseEmptyMessage = 'Every intake or cohort belongs to a tenant course.',
  courseEmptyActionLabel = 'Create Course',
  emptyTitle = 'No intakes yet.',
  emptyMessage = 'Create an open intake or a dated cohort for your public academy page.',
  emptyActionLabel = 'Create Intake',
  modalTitle = 'New Intake',
  modalDescription = 'Set up an open intake or cohort for a course.',
  saveLabel = 'Save Intake',
}) {
  const { user } = useAuth();
  const { activeTenantId } = useTenant();
  const { notify } = useToast();
  const navigate = useNavigate();
  const [intakes, setIntakes] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState(createInitialValues());

  const load = useCallback(async () => {
    setLoading(true);
    const [nextIntakes, nextCourses] = await Promise.all([getIntakes(activeTenantId), getCourses(activeTenantId)]);
    setIntakes(nextIntakes);
    setCourses(nextCourses);
    setValues((current) => ({
      ...current,
      courseId: current.courseId || nextCourses[0]?.id || '',
    }));
    setLoading(false);
  }, [activeTenantId]);

  useEffect(() => {
    load().catch((error) => {
      console.error(error);
      setLoading(false);
    });
  }, [load]);

  function updateSession(index, patch) {
    setValues((current) => ({
      ...current,
      sessions: current.sessions.map((session, sessionIndex) => (sessionIndex === index ? { ...session, ...patch } : session)),
    }));
  }

  function addSession() {
    setValues((current) => ({
      ...current,
      sessions: [...current.sessions, createDefaultIntakeSession()],
    }));
  }

  function removeSession(index) {
    setValues((current) => ({
      ...current,
      sessions: current.sessions.length > 1 ? current.sessions.filter((_, sessionIndex) => sessionIndex !== index) : current.sessions,
    }));
  }

  async function onSubmit(event) {
    event.preventDefault();
    if (!courses.length) return;
    setSaving(true);
    try {
      await createIntake(activeTenantId, user.uid, values);
      notify('Intake created successfully.');
      setValues(createInitialValues(courses[0]?.id || ''));
      setOpen(false);
      await load();
    } catch (error) {
      console.error(error);
      notify('Unable to create intake. Try again.', 'error');
    } finally {
      setSaving(false);
    }
  }

  const courseName = (courseId) => courses.find((course) => course.id === courseId)?.name || '-';
  const openIntakes = intakes.filter((item) => item.status === 'open').length;
  const cohortIntakes = intakes.filter((item) => item.type === 'cohort').length;
  const scheduledIntakes = intakes.filter((item) => item.startDate).length;

  return (
    <div className="page-stack operations-page intakes-page">
      <section className="intakes-intro">
        <div className="intakes-intro-copy">
          <span className="intakes-kicker">Admissions planning</span>
          <h1>Give every learner<br /><i>a clear way in.</i></h1>
          <p>{description} Shape the dates, sessions and capacity once, then let your public academy page guide the right people into the right learning experience.</p>
        </div>
        <div className="intakes-intro-actions"><Button icon={Plus} onClick={() => setOpen(true)}>{actionLabel}</Button></div>
      </section>

      <section className="intakes-snapshot" aria-label="Intake planning overview">
        <article className="intakes-snapshot-card intakes-snapshot-card--primary"><span><CalendarCheck2 size={16} /></span><div><small>Total intakes</small><strong>{intakes.length}</strong><p>Every route into your courses.</p></div></article>
        <article className="intakes-snapshot-card intakes-snapshot-card--green"><span><CheckCircle2 size={16} /></span><div><small>Open for applications</small><strong>{openIntakes}</strong><p>Ready for learners to join.</p></div></article>
        <article className="intakes-snapshot-card intakes-snapshot-card--blue"><span><UsersRound size={16} /></span><div><small>Cohort journeys</small><strong>{cohortIntakes}</strong><p>Structured groups with a start.</p></div></article>
        <article className="intakes-snapshot-card intakes-snapshot-card--warm"><span><Clock3 size={16} /></span><div><small>Dated schedules</small><strong>{scheduledIntakes}</strong><p>Intakes with a defined beginning.</p></div></article>
      </section>
      {!courses.length && !loading ? (
        <EmptyState title={courseEmptyTitle} message={courseEmptyMessage} actionLabel={courseEmptyActionLabel} to="/dashboard/courses" icon={CalendarPlus} />
      ) : loading ? (
        <LoadingState label="Loading intakes..." />
      ) : (
        <section className="intakes-board">
          <div className="intakes-board-heading"><div><span className="intakes-section-kicker">Your routes into learning</span><h2>Intake board</h2><p>Organise open entry and guided cohorts around how your learners actually join.</p></div><span className="intakes-board-meta"><Globe2 size={14} /> Public enrolment ready</span></div>
          {intakes.length ? (
            <div className="intake-card-grid">
              {intakes.map((item, index) => (
                <article className="intake-card" key={item.id}>
                  <div className="intake-card-top"><span className="intake-card-index">{String(index + 1).padStart(2, '0')}</span><StatusBadge status={item.status} /></div>
                  <h3>{item.name}</h3>
                  <p className="intake-card-course">{courseName(item.courseId)}</p>
                  <div className="intake-card-facts">
                    <span><Clock3 size={14} /><b>{formatIntakeSessionsSummary(item.sessions)}</b></span>
                    <span><CalendarCheck2 size={14} /><b>{item.startDate ? formatDate(item.startDate) : 'Open start'}</b></span>
                    <span><UsersRound size={14} /><b>{item.capacity || 'Unlimited'} places</b></span>
                  </div>
                  <div className="intake-card-footer"><span className={item.publicVisible ? 'is-visible' : 'is-hidden'}>{item.publicVisible ? 'Visible publicly' : 'Private intake'}</span><button type="button" className="intake-card-open" onClick={() => navigate(`/dashboard/intakes/${item.id}`)}>View details <ArrowRight size={15} /></button></div>
                </article>
              ))}
            </div>
          ) : <EmptyState title={emptyTitle} message={emptyMessage} actionLabel={emptyActionLabel} onAction={() => setOpen(true)} icon={CalendarPlus} />}
        </section>
      )}

      <Modal className="operation-modal" open={open} onClose={() => setOpen(false)} title={modalTitle} description={modalDescription}>
        <form onSubmit={onSubmit} className="modal-form">
          <FormField label="Intake / Cohort Name" id="intake-name">
            <input id="intake-name" required value={values.name} onChange={(event) => setValues({ ...values, name: event.target.value })} placeholder="January 2027 Cohort" />
          </FormField>
          <FormField label="Course" id="intake-course">
            <select id="intake-course" required value={values.courseId} onChange={(event) => setValues({ ...values, courseId: event.target.value })}>
              {courses.map((course) => <option key={course.id} value={course.id}>{course.name}</option>)}
            </select>
          </FormField>
          <div className="form-grid">
            <FormField label="Type" id="intake-type">
              <select id="intake-type" value={values.type} onChange={(event) => setValues({ ...values, type: event.target.value })}>
                <option value="cohort">Cohort</option>
                <option value="open">Open Intake</option>
              </select>
            </FormField>
            <FormField label="Status" id="intake-status">
              <select id="intake-status" value={values.status} onChange={(event) => setValues({ ...values, status: event.target.value })}>
                <option value="open">open</option>
                <option value="closed">closed</option>
                <option value="draft">draft</option>
              </select>
            </FormField>
          </div>
          <div className="form-grid">
            <FormField label="Start Date" id="intake-start">
              <input id="intake-start" type="date" value={values.startDate} onChange={(event) => setValues({ ...values, startDate: event.target.value })} />
            </FormField>
            <FormField label="End Date" id="intake-end">
              <input id="intake-end" type="date" value={values.endDate} onChange={(event) => setValues({ ...values, endDate: event.target.value })} />
            </FormField>
          </div>
          <FormField label="Capacity" id="intake-capacity">
            <input id="intake-capacity" type="number" min="0" placeholder="0 means unlimited" value={values.capacity} onChange={(event) => setValues({ ...values, capacity: event.target.value })} />
          </FormField>

          <section className="session-builder">
            <div className="panel-heading">
              <h3>Session options</h3>
              <span>Students can pick from these time blocks when they apply.</span>
            </div>
            {values.sessions.map((session, index) => (
              <article key={`${index}-${session.label || session.startTime}`} className="session-card">
                <div className="form-grid">
                  <FormField label="Session label" id={`session-label-${index}`}>
                    <input
                      id={`session-label-${index}`}
                      value={session.label}
                      onChange={(event) => updateSession(index, { label: event.target.value })}
                      placeholder="Weekday morning"
                    />
                  </FormField>
                  <FormField label="Start time" id={`session-start-${index}`}>
                    <input
                      id={`session-start-${index}`}
                      type="time"
                      value={session.startTime}
                      onChange={(event) => updateSession(index, { startTime: event.target.value })}
                    />
                  </FormField>
                  <FormField label="End time" id={`session-end-${index}`}>
                    <input
                      id={`session-end-${index}`}
                      type="time"
                      value={session.endTime}
                      onChange={(event) => updateSession(index, { endTime: event.target.value })}
                    />
                  </FormField>
                </div>
                <div className="weekday-grid">
                  {WEEKDAY_OPTIONS.map((day) => (
                    <label key={day.value} className="checkbox-row">
                      <input
                        type="checkbox"
                        checked={session.daysOfWeek.includes(day.value)}
                        onChange={(event) => {
                          const nextDays = event.target.checked
                            ? [...session.daysOfWeek, day.value]
                            : session.daysOfWeek.filter((value) => value !== day.value);
                          updateSession(index, { daysOfWeek: nextDays });
                        }}
                      />
                      {day.label}
                    </label>
                  ))}
                </div>
                {values.sessions.length > 1 ? (
                  <Button type="button" variant="secondary" icon={Trash2} onClick={() => removeSession(index)}>
                    Remove session
                  </Button>
                ) : null}
              </article>
            ))}
            <Button type="button" variant="secondary" icon={Plus} onClick={addSession}>Add session option</Button>
          </section>

          <label className="checkbox-row">
            <input type="checkbox" checked={values.publicVisible} onChange={(event) => setValues({ ...values, publicVisible: event.target.checked })} />
            Show this intake publicly
          </label>
          <div className="form-actions">
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" loading={saving} disabled={!courses.length}>{saveLabel}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
